import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { officers } from '../../data/reporting';
import { formatSeconds } from '../../utils/format';
import { contactRateHealth, healthText, pastDueHealth, speedHealth } from '../../utils/health';
const th = 'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400';
const td = 'px-4 py-4 text-sm text-ink-700';
function statusDot(health: 'healthy' | 'watch' | 'critical') {
  const color = health === 'healthy' ? 'bg-emerald-500' : health === 'watch' ? 'bg-amber-500' : 'bg-rose-500';
  return <span className={`h-2 w-2 shrink-0 rounded-full ${color}`} aria-hidden="true" data-id="element-283" data-mp="2ennzwtxxfn14" />;
}
export function OfficerTable() {
  const navigate = useNavigate();
  const sorted = [...officers].sort((a, b) => b.pastDuePct - a.pastDuePct);
  return <Card className="overflow-hidden" data-id="element-284" data-mp="2tjwwfnqfnm9u">
      <div className="flex items-center justify-between border-b border-ink-200 px-6 py-4" data-id="element-285" data-mp="2tky8nvj6qet5">
        <div data-id="element-286" data-mp="1zrhwdbpwwjsl">
          <h2 className="text-sm font-semibold text-ink-900" data-id="element-287" data-mp="1zs9ogckavze4">Loan Officer Performance</h2>
          <p className="mt-0.5 text-xs text-ink-500" data-id="element-288" data-mp="1uq0vhg7fgxc0">
            Ranked by past due percentage — select an officer to drill in
          </p>
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-289" data-mp="1lrfevqlinsdv">
        <table className="w-full min-w-[980px]" data-id="element-290" data-mp="1lsia2apvggzl">
          <thead className="border-b border-ink-200 bg-ink-50/60" data-id="element-291" data-mp="2ihptnd6xkg0v">
            <tr data-id="element-292" data-mp="2ijarbbojkf2y">
              <th scope="col" className={th} data-id="element-293" data-mp="2di3jjcmeffwa">
                Loan Officer
              </th>
              <th scope="col" className={th} data-id="element-294" data-mp="28rdc7tuex2yy">
                Assigned
              </th>
              <th scope="col" className={th} data-id="element-295" data-mp="28p0losal9kaa">
                Worked Today
              </th>
              <th scope="col" className={th} data-id="element-296" data-mp="24np0r02ntn8f">
                Remaining
              </th>
              <th scope="col" className={th} data-id="element-297" data-mp="319se6zmp0gge">
                Past Due %
              </th>
              <th scope="col" className={th} data-id="element-298" data-mp="2wfo80il94cgd">
                Calls
              </th>
              <th scope="col" className={th} data-id="element-299" data-mp="ujxgtxaoe5u2">
                SMS
              </th>
              <th scope="col" className={th} data-id="element-300" data-mp="zhgw90j40onl">
                Emails
              </th>
              <th scope="col" className={th} data-id="element-301" data-mp="kudv8jvkvxvi">
                Speed to Lead
              </th>
              <th scope="col" className={th} data-id="element-302" data-mp="prxann40igp1">
                Contact Rate
              </th>
              <th scope="col" className={th} aria-label="Open" data-id="element-303" data-mp="bseb3ip9odoc" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-200" data-id="element-304" data-mp="gsk00r45jm2q">
            {sorted.map(officer => {
            const pd = pastDueHealth(officer.pastDuePct);
            return <tr key={officer.id} tabIndex={0} role="link" onClick={() => navigate(`/reporting/officer/${officer.id}`)} onKeyDown={e => {
              if (e.key === 'Enter') navigate(`/reporting/officer/${officer.id}`);
            }} className="cursor-pointer transition-colors hover:bg-ink-50 focus:bg-ink-50 focus:outline-none" data-id="element-305" data-mp="6tunldv0yttv">
                  <td className={`${td} font-medium text-ink-900`} data-id="element-306" data-mp="3goqmi4tgdafw">
                    <span className="flex items-center gap-3" data-id="element-307" data-mp="3vghlmc9136iw">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink-100 text-[11px] font-semibold text-ink-700" data-id="element-308" data-mp="3qp99v9k8792k">
                        {officer.initials}
                      </span>
                      {officer.name}
                    </span>
                  </td>
                  <td className={`${td} tabular`} data-id="element-309" data-mp="i9posxtpkwyl">{officer.leadsAssigned}</td>
                  <td className={`${td} tabular`} data-id="element-310" data-mp="da3b6n9yxep9">{officer.leadsWorkedToday}</td>
                  <td className={`${td} tabular`} data-id="element-311" data-mp="d80a987a215r">{officer.leadsRemaining}</td>
                  <td className={`${td} tabular font-semibold ${healthText[pd]}`} data-id="element-312" data-mp="2k12q7cwgwmpw">
                    <span className="flex items-center gap-2" data-id="element-313" data-mp="2f2hmv4lx5mx8">
                      {statusDot(pd)}
                      {officer.pastDuePct}%
                    </span>
                  </td>
                  <td className={`${td} tabular`} data-id="element-314" data-mp="2lgoo6z2qs45q">{officer.pastDueCalls}</td>
                  <td className={`${td} tabular`} data-id="element-315" data-mp="2qg9njaltqeiz">{officer.pastDueSms}</td>
                  <td className={`${td} tabular`} data-id="element-316" data-mp="2qeylpgbxvgui">{officer.pastDueEmails}</td>
                  <td className={`${td} tabular font-medium ${healthText[speedHealth(officer.speedToLeadSeconds)]}`} data-id="element-317" data-mp="2vf4dqxm4f5d4">
                    {formatSeconds(officer.speedToLeadSeconds)}
                  </td>
                  <td className={`${td} tabular font-medium ${healthText[contactRateHealth(officer.contactRate)]}`} data-id="element-318" data-mp="27prxjdsdlx1b">
                    {officer.contactRate}%
                  </td>
                  <td className={`${td} text-right`} data-id="element-319" data-mp="1f0l1clge9jj8">
                    <ChevronRightIcon className="h-4 w-4 text-ink-300" aria-hidden="true" data-id="element-320" data-mp="1k0qw4ahynm5j" />
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
}